import dayjs from 'dayjs';
import { ORDER_TYPE } from '../constants/routingStatusConstant';
import { Address4, Address6 } from 'ip-address';

const DATE_FORMAT = 'YYYY-MM-DD';

const formatSummaryRes = data => {
  const formattedData = {};
  const uid = data.uid;
  const members = data.members;

  if (!members || !uid) return formattedData;

  formattedData.uid = uid;
  formattedData.members = [];

  Object.keys(members).forEach((membersKey, index) => {
    const memberData = members[membersKey];
    const { memAccount = '', regions = {} } = memberData;
    const asnlist = [];

    Object.keys(regions).forEach(regionName => {
      const { economies = {} } = regions[regionName];

      Object.keys(economies).forEach(economy => {
        const { asns = {}, name } = economies[economy];

        Object.keys(asns).forEach(asn => {
          asnlist.push({
            asn: asn,
            economy: economy,
            economyName: name,
            region: regionName,
            short_name: asns[asn].name
          });
        });
      });
    });

    formattedData.members[index] = {
      asnlist: asnlist,
      'mem-account': memAccount,
      memid: parseInt(membersKey),
      // economy and region is used to keep the info of selected ASN, even though all ASNs belonging to a member may have different economy and region property
      // if current economy and region property in Member is the same as the one of the selected ASN, this structure could avoid retrieve data repeatedly
      economy: asnlist[0] ? asnlist[0].economy : '',
      region: asnlist[0] ? asnlist[0].region : ''
    };
  });

  // Sort members alphabetically by 'mem-account'
  formattedData.members &&
    formattedData.members.length &&
    formattedData.members.sort((a, b) => {
      if (a['mem-account'] > b['mem-account']) return 1;
      if (a['mem-account'] < b['mem-account']) return -1;

      return 0;
    });

  return formattedData;
};

const formatHitsRes = (hitList, startDate) => {
  const formattedData = [];

  hitList.forEach((hit, index) => {
    formattedData.push({
      count: hit,
      date: dayjs(startDate)
        .add(index, 'day')
        .format(DATE_FORMAT)
    });
  });
  return formattedData;
};

const formatRoutes = (routingStatusList, myPrefixesList, isForMyPrefixes) => {
  return routingStatusList.map((routingStatus, index) => {
    // Set whether the user is the prefix holder by checking if delegated_prefix.prefix is in myPrefixesList
    let isPrefixHolder =
      isForMyPrefixes ||
      !routingStatus.delegated_prefix ||
      !routingStatus.delegated_prefix.prefix
        ? true
        : myPrefixesList.includes(routingStatus.delegated_prefix.prefix);

    const delegatedPrefix =
      routingStatus.delegated_prefix && routingStatus.delegated_prefix.prefix
        ? routingStatus.delegated_prefix.prefix
        : 'Delegation not found';

    let aggregations =
      routingStatus.delegated_prefix &&
      routingStatus.delegated_prefix.sub_delegations;

    // Check if the user is the holder of each prefix in aggregations
    if (!isForMyPrefixes && aggregations) {
      isPrefixHolder = true;

      aggregations.forEach((item, index) => {
        if (myPrefixesList.includes(item)) return;

        aggregations[index] = `*${item}`;
        isPrefixHolder = false;
      });
    }

    // Convert prefix to its decimal range, i.e. the object of { startAddressDecimalNumber, endAddressDecimalNumber }
    const prefixDecimalRange = convertPrefixToDecimalRange(
      delegatedPrefix.trim()
    );

    // Convert BGP route to its decimal range, i.e. the object of { startAddressDecimalNumber, endAddressDecimalNumber }
    const BGPRoute = routingStatus.route.prefix || '';
    const BGPRouteDecimalRange = convertPrefixToDecimalRange(BGPRoute.trim());

    return {
      ...routingStatus,
      isPrefixHolder,
      delegatedPrefix,
      aggregations,
      prefixDecimalRange,
      BGPRouteDecimalRange
    };
  });
};

const sortRoutes = (routingStatusList, { orderField, orderType }) => {
  if (!orderField || !orderType) return routingStatusList;

  const order = orderType === ORDER_TYPE.ASC ? 1 : -1;

  let list = [];

  if (orderField.indexOf('delegatedPrefix') > -1) {
    list = [...routingStatusList].sort((a, b) => {
      // When prefix ascending is selected, sort prefix by increasing start address decimal number value and decreasing end value (when start value is the same)
      if (
        a.prefixDecimalRange.startAddressDecimalNumber >
        b.prefixDecimalRange.startAddressDecimalNumber
      )
        return order;

      if (
        a.prefixDecimalRange.startAddressDecimalNumber <
        b.prefixDecimalRange.startAddressDecimalNumber
      )
        return -order;

      if (
        a.prefixDecimalRange.startAddressDecimalNumber ==
        b.prefixDecimalRange.startAddressDecimalNumber
      ) {
        if (
          a.prefixDecimalRange.endAddressDecimalNumber <
          b.prefixDecimalRange.endAddressDecimalNumber
        )
          return order;
        if (
          a.prefixDecimalRange.endAddressDecimalNumber >
          b.prefixDecimalRange.endAddressDecimalNumber
        )
          return -order;

        return 0;
      }

      return 0;
    });
  }

  if (orderField.indexOf('roa_status') > -1) {
    list = [...routingStatusList].sort((a, b) => {
      if (a.roa_status > b.roa_status) return order;
      if (a.roa_status < b.roa_status) return -order;
      return 0;
    });
  }

  if (orderField.indexOf('route_object_status') > -1) {
    list = [...routingStatusList].sort((a, b) => {
      if (a.route_object_status > b.route_object_status) return order;
      if (a.route_object_status < b.route_object_status) return -order;
      return 0;
    });
  }

  if (orderField.indexOf('route.prefix') > -1) {
    list = [...routingStatusList].sort((a, b) => {
      // When BGP route ascending is selected, sort it by increasing start address decimal number value and decreasing end value (if start values are the same)
      if (
        a.BGPRouteDecimalRange.startAddressDecimalNumber >
        b.BGPRouteDecimalRange.startAddressDecimalNumber
      )
        return order;

      if (
        a.BGPRouteDecimalRange.startAddressDecimalNumber <
        b.BGPRouteDecimalRange.startAddressDecimalNumber
      )
        return -order;

      if (
        a.BGPRouteDecimalRange.startAddressDecimalNumber ==
        b.BGPRouteDecimalRange.startAddressDecimalNumber
      ) {
        if (
          a.BGPRouteDecimalRange.endAddressDecimalNumber <
          b.BGPRouteDecimalRange.endAddressDecimalNumber
        )
          return order;
        if (
          a.BGPRouteDecimalRange.endAddressDecimalNumber >
          b.BGPRouteDecimalRange.endAddressDecimalNumber
        )
          return -order;

        return 0;
      }

      return 0;
    });
  }

  if (orderField.indexOf('route.asn') > -1) {
    list = [...routingStatusList].sort((a, b) => {
      if (a.route.asn > b.route.asn) return order;

      if (a.route.asn < b.route.asn) return -order;

      return 0;
    });
  }

  return list;
};

// Convert prefix to decimal range by calculating its start and end address decimal number.
// Make use of lib "ip-address" to convert prefix to start and end address.
// Use javascript primitive object BigInt, as the decimal range is very big numbers. The BigInt accepts a string value or an integer value, and convert it to a BitInt value, which can represent integers with arbitrary length
const convertPrefixToDecimalRange = prefix => {
  const addressIpv4 = new Address4(prefix);
  const isValidIpv4 = addressIpv4.isValid();
  const addressIpv6 = new Address6(prefix);
  const isValidIpv6 = addressIpv6.isValid();
  let startAddressDecimalNumber = BigInt(0);
  let endAddressDecimalNumber = BigInt(0);

  if (isValidIpv4) {
    startAddressDecimalNumber = BigInt(
      addressIpv4
        .startAddress()
        .bigInteger()
        .toString()
    );
    endAddressDecimalNumber = BigInt(
      addressIpv4
        .endAddress()
        .bigInteger()
        .toString()
    );
  }

  if (isValidIpv6) {
    startAddressDecimalNumber = BigInt(
      addressIpv6
        .startAddress()
        .bigInteger()
        .toString()
    );
    endAddressDecimalNumber = BigInt(
      addressIpv6
        .endAddress()
        .bigInteger()
        .toString()
    );
  }

  return {
    startAddressDecimalNumber,
    endAddressDecimalNumber
  };
};

export { formatSummaryRes, formatHitsRes, formatRoutes, sortRoutes };
